/**
 * Data Upload — the customer-side uploader for Barratt templates. Templates
 * come from the API; a period and (optionally) a supplier travel with the
 * file so Supplier Performance / Quality uploads mark that supplier's month
 * as done in Supplier Upload History.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import FileDropZone from '../../components/common/FileDropZone';
import { Upload, RefreshCw } from 'lucide-react';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { useDataUpload, ALLOWED_FILE_ACCEPT } from './data-upload/useDataUpload';
import UploadTargetCard from './data-upload/UploadTargetCard';
import ImportStatusBadge from './data-upload/ImportStatusBadge';
import ImportHistoryTable from './data-upload/ImportHistoryTable';
import ImportDetailsDialog from './data-upload/ImportDetailsDialog';

export default function DataUpload() {
  const confirm = useConfirm();
  const upload = useDataUpload();

  const handleDeleteImport = async (importId: number) => {
    const ok = await confirm({
      title: 'Delete Import',
      description: 'Are you sure you want to delete this import record?',
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    await upload.deleteImport(importId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Data Upload</h1>
          <p className="text-muted-foreground">Upload data files using Barratt-specific templates</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void upload.fetchImportHistory()} disabled={upload.isLoadingHistory}>
          <RefreshCw className={`h-4 w-4 mr-2 ${upload.isLoadingHistory ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UploadTargetCard {...upload} />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload File
            </CardTitle>
            <CardDescription>Drag and drop or click to select a file</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Customer review 11 Sep (drag and drop everywhere): shared FileDropZone. */}
            <FileDropZone
              accept={ALLOWED_FILE_ACCEPT}
              disabled={!upload.selectedTemplate}
              busy={upload.isUploading}
              label={!upload.selectedTemplate ? 'Select a template first' : undefined}
              hint="Supported formats: CSV, XLSX, XLS, PDF, DOC, DOCX, PNG, JPG, JPEG, GIF"
              onFiles={(files) => void upload.handleFileUpload(files[0])}
              className="p-8"
              data-testid="barratt-upload-drop-zone"
            />

            {upload.isUploading && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Uploading...</span>
                  <span className="font-medium">{upload.uploadProgress}%</span>
                </div>
                <Progress value={upload.uploadProgress} />
              </div>
            )}

            {upload.currentImport && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Import #{upload.currentImport.id}</span>
                  <ImportStatusBadge {...upload.currentImport} />
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Records:</span>
                    <span className="ml-2 font-medium">{upload.currentImport.total_records}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pages:</span>
                    <span className="ml-2 font-medium">
                      {upload.currentImport.imported_pages}/{upload.currentImport.total_pages}
                    </span>
                  </div>
                </div>
                {upload.currentImport.tracking && (
                  <p className="text-sm text-muted-foreground">
                    Marked as uploaded for{' '}
                    <span className="font-medium text-foreground">{upload.currentImport.tracking.supplier_name}</span>
                  </p>
                )}
                {upload.currentImport.progress_percentage !== undefined && (
                  <Progress value={upload.currentImport.progress_percentage} />
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Import History</CardTitle>
          <CardDescription>Recent data imports and their status</CardDescription>
        </CardHeader>
        <CardContent>
          <ImportHistoryTable
            imports={upload.importHistory}
            loading={upload.isLoadingHistory}
            error={upload.historyError}
            onRetry={() => void upload.fetchImportHistory()}
            templateNameFor={upload.templateNameFor}
            onView={(imp) => void upload.details.view(imp)}
            onDelete={(id) => void handleDeleteImport(id)}
          />
        </CardContent>
      </Card>

      <ImportDetailsDialog
        open={upload.details.open}
        onOpenChange={upload.details.setOpen}
        importSummary={upload.details.importSummary}
        logs={upload.details.logs}
        exceptions={upload.details.exceptions}
      />
    </div>
  );
}
